import { useCallback, useEffect, useState } from 'react';
import { desglosarVenta, formatearCOP } from '@tanteo/compartido';
import {
  cambiarEstadoImpuesto,
  crearImpuesto,
  editarImpuesto,
  eliminarImpuesto,
  ETIQUETA_TIPO_IMPUESTO,
  listarImpuestos,
  TIPOS_IMPUESTO,
  type Impuesto,
  type TipoImpuesto,
} from '../../lib/api-cuentas';
import {
  Boton,
  Campo,
  CampoLista,
  CampoNumero,
  EncabezadoTabla,
  FilaAcciones,
  FilaTabla,
  MensajeError,
  TablaAncha,
  Tarjeta,
  ThAcciones,
} from '../../ui/base';
import { Modal } from '../../ui/Modal';
import { BotonesFila, ConfirmarEliminar, EncabezadoSeccion, EstadoActivo } from './partes';

type Edicion = { impuesto: Impuesto | null } | null;

/** Los impuestos que los productos pueden llevar. Cada comprobante congela la tarifa con la que se vendió. */
export function SeccionImpuestos({ negocioId }: { negocioId: string }) {
  const [impuestos, setImpuestos] = useState<Impuesto[]>([]);
  const [edicion, setEdicion] = useState<Edicion>(null);
  const [aEliminar, setAEliminar] = useState<Impuesto | null>(null);
  const [aviso, setAviso] = useState('');
  const [errorEliminar, setErrorEliminar] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    listarImpuestos(negocioId, false)
      .then(setImpuestos)
      .catch(() => setAviso('No pudimos cargar los impuestos.'));
  }, [negocioId]);
  useEffect(cargar, [cargar]);

  async function hacer(accion: () => Promise<void>, alFallar: (m: string) => void): Promise<boolean> {
    setOcupado(true);
    try {
      await accion();
      cargar();
      return true;
    } catch (e) {
      alFallar(e instanceof Error ? e.message : 'No se pudo guardar.');
      return false;
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Tarjeta>
      <EncabezadoSeccion
        titulo="Impuestos"
        ayuda="Un producto puede llevar varios, o ninguno si es exento. Cada tarifa queda congelada en el comprobante al momento de vender."
        agregar="Agregar impuesto"
        onAgregar={() => setEdicion({ impuesto: null })}
        e2eAgregar="nuevo-impuesto"
      />
      <TablaAncha data-e2e="tabla-impuestos">
        <EncabezadoTabla>
          <th className="py-2 text-left">Nombre</th>
          <th className="py-2 text-left">Tipo</th>
          <th className="py-2 text-left">Tarifa</th>
          <th className="py-2 text-left">Sobre</th>
          <th className="py-2 text-left">Estado</th>
          <ThAcciones />
        </EncabezadoTabla>
        <tbody>
          {impuestos.map((i) => (
            <FilaTabla key={i.id}>
              <td className="py-2 pr-2 font-semibold">{i.nombre}</td>
              <td className="py-2 pr-2">{ETIQUETA_TIPO_IMPUESTO[i.tipo]}</td>
              <td className="num py-2 pr-2">{i.tarifa}%</td>
              <td className="py-2 pr-2">{i.reduceBase ? 'con descuento' : 'sin descuento'}</td>
              <td className="py-2 pr-2">
                <EstadoActivo activo={i.activo} />
              </td>
              <td className="py-2">
                <BotonesFila
                  nombre={i.nombre}
                  activo={i.activo}
                  ocupado={ocupado}
                  onEditar={() => setEdicion({ impuesto: i })}
                  onEstado={() => void hacer(() => cambiarEstadoImpuesto(negocioId, i.id, !i.activo), setAviso)}
                  onEliminar={() => {
                    setErrorEliminar('');
                    setAEliminar(i);
                  }}
                />
              </td>
            </FilaTabla>
          ))}
        </tbody>
      </TablaAncha>
      <MensajeError>{aviso}</MensajeError>

      <VentanaImpuesto
        edicion={edicion}
        ocupado={ocupado}
        onCerrar={() => setEdicion(null)}
        onGuardar={(datos, alFallar) => {
          const impuesto = edicion?.impuesto ?? null;
          void hacer(
            () =>
              impuesto
                ? editarImpuesto(negocioId, impuesto.id, datos)
                : crearImpuesto(negocioId, datos.nombre, datos.tipo, datos.tarifa, datos.reduceBase),
            alFallar
          ).then((ok) => ok && setEdicion(null));
        }}
      />
      <ConfirmarEliminar
        titulo={aEliminar ? `¿Eliminar el impuesto «${aEliminar.nombre}»?` : null}
        puntos={[
          'Los productos que lo tienen dejan de cobrarlo desde la próxima venta.',
          'Los comprobantes ya emitidos conservan la tarifa con la que se vendió.',
        ]}
        ocupado={ocupado}
        error={errorEliminar}
        onCerrar={() => setAEliminar(null)}
        onDesactivar={
          aEliminar?.activo
            ? () =>
                void hacer(() => cambiarEstadoImpuesto(negocioId, aEliminar.id, false), setErrorEliminar).then(
                  (ok) => ok && setAEliminar(null)
                )
            : undefined
        }
        onEliminar={() =>
          aEliminar &&
          void hacer(() => eliminarImpuesto(negocioId, aEliminar.id), setErrorEliminar).then((ok) => ok && setAEliminar(null))
        }
      />
    </Tarjeta>
  );
}

function VentanaImpuesto({
  edicion,
  ocupado,
  onCerrar,
  onGuardar,
}: {
  edicion: Edicion;
  ocupado: boolean;
  onCerrar: () => void;
  onGuardar: (
    datos: { nombre: string; tipo: TipoImpuesto; tarifa: number; reduceBase: boolean },
    alFallar: (mensaje: string) => void
  ) => void;
}) {
  const impuesto = edicion?.impuesto ?? null;
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<TipoImpuesto>('iva');
  const [tarifa, setTarifa] = useState<number | null>(19);
  const [reduceBase, setReduceBase] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!edicion) return;
    setNombre(impuesto?.nombre ?? '');
    setTipo(impuesto?.tipo ?? 'iva');
    setTarifa(impuesto?.tarifa ?? 19);
    setReduceBase(impuesto?.reduceBase ?? true);
    setError('');
  }, [edicion, impuesto]);

  const cambiaTarifa = impuesto !== null && tarifa !== impuesto.tarifa;
  const cambiaBase = impuesto !== null && reduceBase !== impuesto.reduceBase;

  return (
    <Modal abierto={edicion !== null} titulo={impuesto ? 'Editar impuesto' : 'Agregar impuesto'} onCerrar={onCerrar}>
      <div className="space-y-4" data-e2e="ventana-impuesto">
        <div className="grid gap-3 sm:grid-cols-3">
          <Campo
            etiqueta="Nombre"
            placeholder="IVA 19%"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            data-e2e="impuesto-nombre"
          />
          <CampoLista etiqueta="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoImpuesto)}>
            {TIPOS_IMPUESTO.map((t) => (
              <option key={t} value={t}>
                {ETIQUETA_TIPO_IMPUESTO[t]}
              </option>
            ))}
          </CampoLista>
          <CampoNumero etiqueta="Tarifa %" min={0} max={60} valor={tarifa} onValor={setTarifa} data-e2e="impuesto-tarifa" />
        </div>
        <CampoLista
          etiqueta="Se cobra sobre"
          value={reduceBase ? 'con' : 'sin'}
          onChange={(e) => setReduceBase(e.target.value === 'con')}
          data-e2e="impuesto-sobre"
        >
          <option value="con">Precio con descuento</option>
          <option value="sin">Precio sin descuento</option>
        </CampoLista>
        {tarifa !== null && <EjemploImpuesto tipo={tipo} tarifa={tarifa} reduceBase={reduceBase} />}
        {cambiaTarifa && (
          <p className="text-muted text-contenido">
            Desde ahora los productos con este impuesto cobran {tarifa}%. Los comprobantes ya emitidos conservan el{' '}
            {impuesto.tarifa}%.
          </p>
        )}
        {cambiaBase && (
          <p className="text-muted text-contenido">
            Desde ahora este impuesto se cobra sobre el precio {reduceBase ? 'con' : 'sin'} descuento. Los comprobantes ya
            emitidos conservan lo que se cobró.
          </p>
        )}
        <MensajeError>{error}</MensajeError>
        <FilaAcciones>
          <Boton variante="contorno" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            disabled={ocupado || nombre.trim().length < 2 || tarifa === null}
            onClick={() => tarifa !== null && onGuardar({ nombre, tipo, tarifa, reduceBase }, setError)}
            data-e2e="agregar-impuesto"
          >
            {impuesto ? 'Guardar cambios' : 'Agregar impuesto'}
          </Boton>
        </FilaAcciones>
      </div>
    </Modal>
  );
}

/** Una venta de ejemplo con el impuesto tal como está escrito, calculada con lo mismo que cobra el mostrador. */
const PRECIO_EJEMPLO = 10000;
const DESCUENTO_EJEMPLO = 10;

function EjemploImpuesto({ tipo, tarifa, reduceBase }: { tipo: TipoImpuesto; tarifa: number; reduceBase: boolean }) {
  const venta = desglosarVenta([
    {
      sku: 'EJEMPLO',
      nombre: 'Ejemplo',
      productoId: 'ejemplo',
      cantidad: 1,
      precioUnit: PRECIO_EJEMPLO,
      impuestos: [{ nombre: 'Ejemplo', tipo, tarifa, reduceBase }],
      descuento: { descuentoId: 'ejemplo', nombre: 'Ejemplo', libre: false, tipo: 'pct', valor: DESCUENTO_EJEMPLO },
    },
  ]);
  const linea = venta.lineas[0]!;
  const base = reduceBase ? linea.neto : linea.bruto;
  const filas: [string, string][] = [
    ['Precio del producto', formatearCOP(linea.bruto)],
    [`Descuento del ${DESCUENTO_EJEMPLO} %`, `− ${formatearCOP(linea.rebaja)}`],
    ['Precio con descuento', formatearCOP(linea.neto)],
  ];
  return (
    <div className="rounded-control bg-surface space-y-1 px-3 py-2 text-contenido" data-e2e="impuesto-ejemplo">
      <p className="text-muted text-etiqueta font-semibold uppercase tracking-wide">Así se calcula en una venta</p>
      {filas.map(([etiqueta, valor]) => (
        <p key={etiqueta} className="flex justify-between gap-3">
          <span>{etiqueta}</span>
          <span className="num">{valor}</span>
        </p>
      ))}
      <p className="flex justify-between gap-3">
        <span>
          Impuesto del {tarifa} % <strong>sobre {formatearCOP(base)}</strong>
          {reduceBase ? '' : ', el precio antes del descuento'}
        </span>
        <span className="num">+ {formatearCOP(linea.impuesto)}</span>
      </p>
      <p className="border-border flex justify-between gap-3 border-t pt-1 font-bold">
        <span>El cliente paga</span>
        <span className="num">{formatearCOP(venta.total)}</span>
      </p>
    </div>
  );
}
